'use client';

import React, { useRef } from 'react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Scribble from '@/app/components/suppliers/Scribble';

const TRIED = [
  'cold cally', 'hromadné e-maily', 'letáky do schránek', 'katalogy poštou',
  'telemarketing', 'sponzoring', 'inzerce v oborových časopisech', 'bannery',
];

export default function ProofSection({ id }: { id: string }) {
  const root = useRef<HTMLElement>(null);
  const numRef = useRef<HTMLSpanElement>(null);

  useGSAP(() => {
    gsap.registerPlugin(ScrollTrigger);
    const obj = { v: 7000 };
    const fmt = (n: number) => Math.round(n).toLocaleString('cs-CZ').replace(/ /g, ' ');
    gsap.to(obj, {
      v: 130, duration: 2.2, ease: 'power4.out',
      scrollTrigger: { trigger: numRef.current, start: 'top 80%', once: true },
      onUpdate: () => { if (numRef.current) numRef.current.textContent = fmt(obj.v); },
    });
    gsap.from('.sup-proof-old', { scale: 1.06, opacity: 0, duration: 0.7, ease: 'power3.out', scrollTrigger: { trigger: root.current, start: 'top 70%', once: true } });
    gsap.from('.sup-proof-para', { y: 24, opacity: 0, duration: 0.8, stagger: 0.15, ease: 'power3.out', scrollTrigger: { trigger: root.current, start: 'top 70%', once: true } });
  }, { scope: root });

  return (
    <section ref={ root } id={ id } className='relative mt-10 md:mt-24'>
      <div className='container'>
        <div className='grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-6 items-start'>
          <div className='lg:col-span-5'>
            <h2 className='sup-h2'>Naučili jsme se<br />to <span className='relative inline-block whitespace-nowrap'>za vlastní.
              <Scribble kind='burst' width={ 5 } duration={ 0.5 } style={ { right: '-0.8em', top: '0.05em', width: '0.55em', height: '0.55em' } } />
            </span></h2>
            <p className='sup-body sup-proof-para mt-8'>
              Vyzkoušeli jsme desítky způsobů, jak oslovit majitele restaurací. Investovali jsme vlastní stovky tisíc korun. Získali jsme zkušenosti.
            </p>
            <p className='sup-lead sup-proof-para mt-6 max-w-[20ch]'>
              Teď nabízíme to nejlepší vám.
            </p>
          </div>

          <div className='lg:col-span-7 lg:pl-8'>
            <div className='relative'>
              <div className='text-[0.95rem] md:text-[1.1rem] font-bold opacity-70'>Cena za relevantní poptávku, když jsme začínali</div>
              <div className='sup-proof-old relative inline-block mt-1'>
                <Scribble kind='arrow' width={ 5 } delay={ 0.9 } className='hidden md:block' style={ { right: '-140px', top: '55%', width: '120px', height: '120px', transform: 'scaleX(-1) rotate(-10deg)' } } />
                <span className='sup-num text-[4.2rem] md:text-[8.5rem] opacity-60'>7 000 Kč</span>
                <Scribble kind='strike' width={ 9 } delay={ 0.3 } style={ { left: '-2%', right: '-2%', top: '28%', height: '46%' } } />
              </div>
            </div>

            <div className='relative mt-6 md:mt-8'>
              <div className='text-[0.95rem] md:text-[1.1rem] font-bold opacity-70'>Dnes</div>
              <div className='relative inline-block mt-6 md:mt-8 ml-8 md:ml-8'>
                <span className='sup-num text-[5.6rem] md:text-[13rem] text-primary'><span ref={ numRef }>7 000</span> Kč</span>
                <Scribble kind='circle' trigger='scrub' start='top 85%' end='top 35%' color='var(--sup-purple)' width={ 8 } style={ { left: '-13%', right: '-13%', top: '-16%', height: '132%' } } />
              </div>
              <div className='sup-lead mt-10 md:mt-12 max-w-[24ch]'>za relevantní poptávku od majitele podniku</div>
            </div>
          </div>
        </div>
      </div>

      {/* what we tried – ticker */}
      <div className='mt-16 md:mt-24 overflow-hidden py-6 -rotate-[1.5deg] scale-[1.03]' aria-label='Co jsme vyzkoušeli'>
        <div className='sup-ticker'>
          { [0, 1].map((dup) => (
            <React.Fragment key={ dup }>
              { TRIED.map((t, i) => (
                <span key={ `${ dup }-${ i }` } className='sup-chip sup-chip--dead' style={ { ['--tilt' as string]: `${ (i % 3 - 1) * 1.6 }deg` } } aria-hidden={ dup === 1 }>{ t }</span>
              )) }
              <span className='sup-chip sup-chip--purple' style={ { ['--tilt' as string]: '-1.2deg' } } aria-hidden={ dup === 1 }>reklama na sítích, kde restauratéři opravdu jsou ✓</span>
            </React.Fragment>
          )) }
        </div>
      </div>
    </section>
  );
}
